import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Form from '../../antd/form/Form/Form.jsx';
import CollapseBlock from '../blocks/CollapseBlock/CollapseBlock.jsx';
import FormTextField from '../../antd/form/FormTextField/FormTextField.jsx';
import FormDate from '../../antd/form/FormDate/FormDate.jsx';
import { email, required } from '../../antd/form/FormValidation.js';
import FormButton from '../../antd/form/FormButton/FormButton.jsx';
import FormButtonsContainer from '../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import PropTypes from 'prop-types';
import FormSelect from '../../antd/form/FormSelect/FormSelect.jsx';
import useUserHasRole from '../../../hooks/useUserHasRoles.jsx';
import { ROLE_EXTERNAL_USERS_ADMIN } from '../../../utils/constants/roles.js';
import { Spinner } from '../index.jsx';
import { useGetExtUsersRolesQuery } from '../../../redux/services/roles.jsx';
import FormHiddenField from '../../antd/form/FormHiddenField/FormHiddenField.jsx';
import FormCheckBox from '../../antd/form/FormCheckBox/FormCheckBox.jsx';
import {
  StyledModal,
  StyledModalHeader,
  ButtonContainer,
  MainButton,
} from '../form/FormElements.jsx';
import GDPRInfoContent from './GDPRInfoContent.jsx';
import QuestionModal from '../modals/QuestionModal/QuestionModal.jsx';
import { SeriousDeleteQuestion } from '../DeleteQuestion/SeriousDeleteQuestion.jsx';

const ExternalUserForm = ({ initials, onSubmit, form, onDelete }) => {
  const { t } = useTranslation();
  const isExtUsersAdmin = useUserHasRole(ROLE_EXTERNAL_USERS_ADMIN);

  // Enable reCAPTCHA only on the specific public route
  const isRecaptchaRoute =
    typeof window !== 'undefined' &&
    window.location.pathname === '/registracia-externeho-pouzivatela';

  const { data: roles, isLoading: isLoadingRoles } = useGetExtUsersRolesQuery(
    undefined,
    {
      skip: !isExtUsersAdmin,
    },
  );

  const rolesOptions = useMemo(
    () =>
      roles &&
      roles.map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [roles],
  );

  // Load reCAPTCHA script on public registration route and toggle badge visibility
  useEffect(() => {
    let script;

    if (isRecaptchaRoute) {
      if (!window.grecaptcha) {
        script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${import.meta.env.VITE_RECAPTCHA_SITE_KEY}`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          const badge = document.querySelector('.grecaptcha-badge');
          if (badge) badge.style.display = '';
        };
        document.body.appendChild(script);
      } else {
        const badge = document.querySelector('.grecaptcha-badge');
        if (badge) badge.style.display = '';
      }
    } else {
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) badge.style.display = 'none';
    }

    return () => {
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) badge.style.display = 'none';
      if (script) document.body.removeChild(script);
    };
  }, [isRecaptchaRoute]);

  // Hide site-wide question icons on this route (visual parity with partners form)
  useEffect(() => {
    if (!isRecaptchaRoute) return;

    const elements = document.getElementsByClassName(
      'application-questions-container',
    );
    const prevDisplays = [];
    for (let i = 0; i < elements.length; i++) {
      prevDisplays[i] = elements[i].style.display;
      elements[i].style.display = 'none';
    }
    return () => {
      for (let i = 0; i < elements.length; i++) {
        elements[i].style.display = prevDisplays[i] || '';
      }
    };
  }, [isRecaptchaRoute]);

  const handleFinish = (values) => {
    if (!isRecaptchaRoute || !window.grecaptcha) {
      onSubmit({ ...values, captchaResponse: '' });
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(import.meta.env.VITE_RECAPTCHA_SITE_KEY, { action: 'contact' })
        .then((token) => {
          onSubmit({ ...values, captchaResponse: token });
        });
    });
  };

  const [gdprOpen, setGdprOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  return (
    <>
      <Form form={form} onFinish={handleFinish} initialValues={initials}>
        <CollapseBlock
          collapsible={!isRecaptchaRoute}
          title={t('Informácie o externom používateľovi')}
        >
          <FormHiddenField name="id" />
          <FormHiddenField name="from" />

          <FormTextField
            label={t('Meno')}
            name="first_name"
            rules={[required(t)]}
          />

          <FormTextField
            label={t('Priezvisko')}
            name="last_name"
            rules={[required(t)]}
          />

          <FormDate
            label={t('Dátum narodenia')}
            name="date_of_birth"
            rules={[
              required(t),
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const selectedDate = new Date(value);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  if (selectedDate >= today) {
                    return Promise.reject(
                      new Error(t('Dátum narodenia musí byť v minulosti')),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />

          <FormTextField
            label={t('E-mailová adresa')}
            name="email"
            rules={[email(t), required(t)]}
            extra={t('E-mailová adresa slúži ako prihlasovacie meno.')}
          />

          <FormTextField
            label={t('Telefónne číslo')}
            name="phone_number"
            extra={t(
              'Ak uvediete telefónne číslo, môžeme vás kontaktovať v prípade zmien.',
            )}
          />

          {isRecaptchaRoute && (
            <FormCheckBox
              name="gdpr_consent"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            t(
                              'Je potrebné udeliť súhlas so spracovaním osobných údajov',
                            ),
                          ),
                        ),
                },
              ]}
              label={
                <span>
                  <span style={{ color: 'red', marginRight: '4px' }}>*</span>
                  {t('Súhlasím so spracovaním osobných údajov podľa ')}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setGdprOpen(true);
                    }}
                  >
                    {t('podmienok spracúvania osobných údajov')}
                  </a>
                </span>
              }
            />
          )}
        </CollapseBlock>
        {isLoadingRoles ? (
          <Spinner />
        ) : (
          isExtUsersAdmin &&
          !isRecaptchaRoute && (
            <CollapseBlock title={t('Role')} collapsible>
              {rolesOptions && (
                <FormSelect
                  label={t('Role používateľa')}
                  name={'user_roles'}
                  placeholder={t('Vyhľadať rolu')}
                  options={rolesOptions}
                  mode="multiple"
                  rules={[required(t)]}
                />
              )}
            </CollapseBlock>
          )
        )}
        <FormButtonsContainer>
          {onDelete ? (
            <FormButton danger onClick={() => setIsDeleteModalOpen(true)}>
              {t('Vymazať účet')}
            </FormButton>
          ) : null}
          <FormButton submitOnClick={true} submitKey={'externalUsers'}>
            {t('Uložiť')}
          </FormButton>
        </FormButtonsContainer>
      </Form>
      <StyledModal
        open={gdprOpen}
        size="md"
        onCancel={() => setGdprOpen(false)}
        footer={null}
        width={900}
      >
        <StyledModalHeader>
          {t('Podmienky spracúvania osobných údajov')}
        </StyledModalHeader>
        <GDPRInfoContent />
        <ButtonContainer>
          <MainButton onClick={() => setGdprOpen(false)}>
            {t('Zatvoriť')}
          </MainButton>
        </ButtonContainer>
      </StyledModal>
      <QuestionModal
        chosenPositive={() => onDelete?.()}
        question={
          <SeriousDeleteQuestion
            firstText={t('Naozaj chcete vymazať svoj používateľský účet?')}
            mainNotice={t(
              'Po vymazaní účtu budete odhlásený zo všetkých FRI akcií. Žiadne informácie o vašich aktivitách v portáli FRI nebudeme uchovávať.',
            )}
            lastText={t(
              'Váš účet zanikne. Operácia sa nedá vrátiť späť.',
            )}
          />
        }
        visible={isDeleteModalOpen}
        yes={t('Vymazať účet')}
        toggle={() => setIsDeleteModalOpen(false)}
        headerTitle={t('Vymazanie účtu')}
      />
    </>
  );
};

ExternalUserForm.propTypes = {
  initials: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  form: PropTypes.object,
  onDelete: PropTypes.func,
};

export default ExternalUserForm;
